import { useNavigate } from 'react-router-dom';
import { useActionServed } from '../../engine/meta/useMeta';
import { Avatar, Badge, EmptyState, Money, Skeleton, Stat, Tree, useApiQuery, useRelationLabel, type ScreenContext, type TreeNode } from '../../engine';
import { formatDateTime } from '../../lib/format';

// Client detail header (ScreenDef.header): identity strip (initials, kind/status, types,
// account manager) + quick stats from GET /v1/clients/:id/summary.

export interface ClientSummary {
  id: string;
  currency: string;
  contacts: number;
  sites: number;
  opportunities: { open: number; won: number; open_one_time_minor: string; open_recurring_minor: string };
  ar_balance_minor: string;
  active_contracts: number;
  open_tickets: number;
  open_tasks: number;
  last_activity_at: string | null;
  types: { id: string; name: string; color: string | null }[];
}

const KIND_TONE: Record<string, 'pos' | 'info' | 'warn' | 'accent' | 'muted'> = { customer: 'pos', prospect: 'info', vendor: 'warn', partner: 'accent', internal: 'muted' };

export function ClientHeader({ id, row }: ScreenContext) {
  // NovaPSA's own roll-up; a connected system (Halo) does not offer it, so it is not asked for there.
  const hasSummary = useActionServed('clients', ':id/summary');
  const summary = useApiQuery<ClientSummary>(['clients', 'summary', id], `/v1/clients/${id}/summary`, { enabled: Boolean(id) && hasSummary });
  const manager = useRelationLabel({ resource: 'technicians', displayField: 'name' }, (row?.account_manager_tech_id as string | null) ?? null);
  const s = summary.data;
  const name = String(row?.name ?? '');
  return (
    <section className="hf-crm-header" aria-label="Client summary">
      <div className="hf-row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Avatar name={name} size="lg" />
        <div className="hf-stack" style={{ gap: 4 }}>
          <div className="hf-row" style={{ flexWrap: 'wrap' }}>
            {row?.ref != null && <span className="hf-mono hf-muted">{String(row.ref)}</span>}
            {row?.kind != null && <Badge tone={KIND_TONE[String(row.kind)] ?? 'muted'}>{String(row.kind)}</Badge>}
            {(s?.types ?? []).map((t) => (
              <Badge key={t.id} tone="accent" dot={false} title="Client type">
                {t.color && <span className="hf-swatch" style={{ background: t.color }} aria-hidden />}
                {t.name}
              </Badge>
            ))}
          </div>
          <div className="hf-row hf-muted" style={{ fontSize: 'var(--t-12)', flexWrap: 'wrap' }}>
            <span>Account manager: {manager.data ?? (row?.account_manager_tech_id ? '…' : 'unassigned')}</span>
            {row?.industry != null && row.industry !== '' && <span>· {String(row.industry)}</span>}
            {s?.last_activity_at && <span>· last activity {formatDateTime(s.last_activity_at)}</span>}
          </div>
        </div>
      </div>
      {summary.isLoading && <Skeleton lines={2} />}
      {s && (
        <div className="hf-stats" data-testid="client-stats">
          <Stat label="Contacts" value={<span className="hf-num">{s.contacts}</span>} sub={`${s.sites} ${s.sites === 1 ? 'site' : 'sites'}`} icon="users" />
          <Stat
            label="Open pipeline"
            value={<Money minor={s.opportunities.open_one_time_minor} currency={s.currency} />}
            sub={
              <>
                {s.opportunities.open} open · <Money minor={s.opportunities.open_recurring_minor} currency={s.currency} /> recurring · {s.opportunities.won} won
              </>
            }
            icon="target"
            accent
          />
          <Stat label="Receivable" value={<Money minor={s.ar_balance_minor} currency={s.currency} color />} sub={`${s.active_contracts} active ${s.active_contracts === 1 ? 'contract' : 'contracts'}`} icon="receipt" />
          <Stat label="Open work" value={<span className="hf-num">{s.open_tickets}</span>} sub={`${s.open_tasks} open ${s.open_tasks === 1 ? 'task' : 'tasks'}`} icon="ticket" />
        </div>
      )}
    </section>
  );
}

interface TreeApiNode {
  id: string;
  name: string;
  ref: string | null;
  kind: string;
  children: TreeApiNode[];
}

/** Hierarchy tab: GET /v1/clients/:id/tree rendered with the engine Tree. */
export function ClientHierarchy({ id }: ScreenContext) {
  const navigate = useNavigate();
  const q = useApiQuery<{ id: string; top_id: string; tree: TreeApiNode }>(['clients', 'tree', id], `/v1/clients/${id}/tree`, { enabled: Boolean(id) });
  if (q.isLoading) return <Skeleton lines={4} />;
  if (!q.data) return <EmptyState title="No hierarchy" icon="list-tree" />;
  const toNode = (n: TreeApiNode): TreeNode => ({ id: n.id, label: n.ref ? `${n.name} · ${n.ref}` : n.name, icon: 'building', badge: n.kind, children: n.children.map(toNode) });
  const root = toNode(q.data.tree);
  if (!root.children?.length && root.id === id) return <EmptyState title="Standalone client" hint="Set a parent client, or make this the parent of others, to build a hierarchy." icon="list-tree" />;
  return <Tree nodes={[root]} selected={id} defaultExpandAll onSelect={(nodeId) => nodeId !== id && navigate(`/clients/${nodeId}`)} aria-label="Client hierarchy" />;
}
